import React from 'react';
import { Typography } from 'antd';
import { StarFilled, MailOutlined } from '@ant-design/icons';
import GmailMark from './GmailMark';
import { ReplyCaptureTier } from '../../types';
import css from './ReplyCaptureTierList.module.scss';

const { Text } = Typography;

interface ReplyCaptureTierListProps {
  forwardingAddress: string;
  onSetup: (tier: ReplyCaptureTier) => void;
}

interface TierConfig {
  tier: ReplyCaptureTier;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  recommended?: boolean;
  ctaLabel: string;
}

const tiers: TierConfig[] = [
  {
    tier: 'addon',
    title: 'Gmail add-on',
    subtitle: 'See application context inside Gmail. Reads only the email you have open.',
    icon: <GmailMark width={22} height={17} />,
    recommended: true,
    ctaLabel: 'Install',
  },
  {
    tier: 'forward',
    title: 'Manual forwarding',
    subtitle: "Forward replies to your Job Beast address. You're in full control.",
    icon: <MailOutlined />,
    ctaLabel: 'Set up',
  },
];

const ReplyCaptureTierList: React.FC<ReplyCaptureTierListProps> = ({
  forwardingAddress,
  onSetup,
}) => (
  <div className={css.list}>
    {tiers.map((cfg, idx) => {
      const isAddon = cfg.tier === 'addon';
      const showAddress = cfg.tier === 'forward';

      return (
        <React.Fragment key={cfg.tier}>
          <div
            className={`${css.row} ${idx === 0 ? css.rowFirst : ''} ${idx === tiers.length - 1 ? css.rowLast : ''}`}
          >
            <div className={`${css.iconWrap} ${isAddon ? css.iconWrapAddon : ''}`}>
              {cfg.icon}
            </div>

            <div className={css.text}>
              <div className={css.titleRow}>
                <span className={css.title}>{cfg.title}</span>
                {cfg.recommended && (
                  <span className={css.recommendedPill}>
                    <StarFilled /> Recommended
                  </span>
                )}
              </div>
              <span className={css.subtitle}>{cfg.subtitle}</span>
            </div>

            <div className={css.action}>
              <button
                className={css.setupButton}
                onClick={() => onSetup(cfg.tier)}
              >
                {cfg.ctaLabel}
              </button>
            </div>
          </div>

          {showAddress && (
            <div className={css.addressSubRow}>
              <Text
                className={css.subAddress}
                copyable={{
                  text: forwardingAddress,
                  tooltips: ['Copy address', 'Copied!'],
                }}
              >
                {forwardingAddress}
              </Text>
              <p className={css.subHelper}>
                Forward any recruiter reply here — we'll match it to the right
                application automatically.
              </p>
            </div>
          )}
        </React.Fragment>
      );
    })}
  </div>
);

export default ReplyCaptureTierList;
